import {
	queryOptions,
	useMutation,
	useQueryClient,
} from "@tanstack/react-query";
import { useIntl } from "react-intl";
import fetcher from "@/core/lib/fetcher";
import type { APIError, APIResponse } from "@/core/types/utils";
import { toast } from "../ui/use-toast";
import {
	AgentStatus,
	type SalesAgent,
	type SalesAgentFormValues,
} from "./salesAgent.types";

// Agents live in booking-service under property/bookings/* so the existing
// api-proxy rule routes them without touching nginx. Do not shorten the path.
const AGENTS_BASE = `${import.meta.env.VITE_API_BOOKING}property/bookings/agents`;

export const salesAgentsQueryOptions = (token: string) =>
	queryOptions({
		queryKey: ["salesAgents", token],
		queryFn: () =>
			fetcher
				.auth(`Bearer ${token}`)
				.get(AGENTS_BASE)
				.json<Promise<APIResponse<SalesAgent[]>>>(),
	});

export const salesAgentQueryOptions = (id: string, token: string) =>
	queryOptions({
		queryKey: ["salesAgent", id, token],
		queryFn: () =>
			fetcher
				.auth(`Bearer ${token}`)
				.get(`${AGENTS_BASE}/${id}`)
				.json<Promise<APIResponse<SalesAgent>>>(),
	});

export const createSalesAgent = (data: SalesAgentFormValues, token: string) =>
	fetcher.auth(`Bearer ${token}`).url(AGENTS_BASE).post(data).json();

export const updateSalesAgent = (
	id: string,
	data: SalesAgentFormValues,
	token: string,
) =>
	fetcher.auth(`Bearer ${token}`).url(`${AGENTS_BASE}/${id}`).put(data).json();

export const updateSalesAgentStatus = (
	id: string,
	status: AgentStatus,
	token: string,
) =>
	fetcher
		.auth(`Bearer ${token}`)
		.url(`${AGENTS_BASE}/${id}/status`)
		.patch({ status })
		.json();

export const useUpdateSalesAgentStatus = (token: string) => {
	const queryClient = useQueryClient();
	const intl = useIntl();
	return useMutation({
		mutationFn: ({ id, status }: { id: string; status: AgentStatus }) =>
			updateSalesAgentStatus(id, status, token),
		onSuccess: (_data, variables) => {
			void queryClient.invalidateQueries({ queryKey: ["salesAgents"] });
			void queryClient.invalidateQueries({ queryKey: ["salesAgent"] });
			toast({
				title: intl.formatMessage({
					id:
						variables.status === AgentStatus.Archived
							? "salesAgents.toastArchived"
							: "salesAgents.toastReactivated",
				}),
			});
		},
		onError: (error) => {
			const errorObject: APIError<null> = JSON.parse(error.message);
			toast({
				title: intl.formatMessage({ id: "common.error" }),
				description: errorObject.message,
				variant: "destructive",
			});
		},
	});
};
